import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Paper, Typography, Button, IconButton, TextField,
  MenuItem, Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItemButton, ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UndoIcon from "@mui/icons-material/Undo";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import WidgetsIcon from "@mui/icons-material/Widgets";
import {
  NAVY, PAGE_BG, BORDER, TEXT_PRIMARY, TEXT_SECONDARY, ERROR,
} from "../../../theme/tokens";
import {
  useGetTemplateQuery, useUpdateTemplateMutation, useReplaceTemplateZonesMutation,
  useGetMediaQuery,
} from "../../../Api/attoDeskApi";
import { IMedia } from "../../../Api/Interface/api.interface";
import { indexMedia, validateZones, ZoneProblem } from "./mediaUtils";
import LayoutPresetsPanel from "./LayoutPresetsPanel";
import SmartLayoutDialog from "./SmartLayoutDialog";
import AiTemplateDialog from "./AiTemplateDialog";
import { LayoutPreset, PresetZone, presetCanBeAdded } from "./presets";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import { extractBackendError } from "../../../Core/extractBackendError";
import Canvas from "./Canvas";
import Inspector from "./Inspector";
import TemplatePreviewDialog from "../TemplatePreviewDialog";
import {
  DesignerTemplate, DesignerZone, WidgetType,
  WIDGET_LABELS, WIDGET_TYPES, defaultConfigFor, nextUid,
} from "./types";

const ORIENTATIONS: Array<{ value: "landscape" | "portrait"; w: number; h: number; label: string }> = [
  { value: "landscape", w: 1920, h: 1080, label: "Landscape 1920×1080" },
  { value: "portrait",  w: 1080, h: 1920, label: "Portrait 1080×1920" },
];

const TemplateEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tid = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading } = useGetTemplateQuery(tid, { skip: !tid });
  const [updateTemplate] = useUpdateTemplateMutation();
  const [replaceZones, { isLoading: saving }] = useReplaceTemplateZonesMutation();

  const [template, setTemplate] = useState<DesignerTemplate | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [interactUid, setInteractUid] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [problems, setProblems] = useState<ZoneProblem[] | null>(null);
  const [paletteTab, setPaletteTab] = useState<"widgets" | "layouts">("widgets");
  const [undoStack, setUndoStack] = useState<DesignerZone[][]>([]);
  const [pendingPreset, setPendingPreset] = useState<LayoutPreset | null>(null);
  const [smartOpen, setSmartOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Company the template belongs to — scopes media library + uploads.
  const companyId: number | undefined = (data as any)?.data?.company_id ?? undefined;

  // Resolve every image/video zone's media_id to a real record so the
  // canvas and inspector can show previews. This mirrors how the player
  // resolves media server-side — the zone JSON only stores media_id, never
  // a duplicated file — so old templates (media_id only) preview correctly.
  const { data: mediaData } = useGetMediaQuery(
    { company_id: companyId },
    { skip: !tid },
  );
  // Media just picked/uploaded via the zone picker. The picker uploads through
  // the non-RTK upload helper, so the page-level media query above does NOT
  // auto-refetch — a freshly uploaded/selected media would otherwise resolve as
  // "Media #N (not found in library)" until a full reload. We merge the picked
  // record in immediately so previews resolve right away. Transient/in-memory
  // only — nothing extra is saved (the zone still stores just media_id).
  const [pickedMedia, setPickedMedia] = useState<Record<number, IMedia>>({});

  const mediaById: Record<number, IMedia> = useMemo(() => {
    const list = Array.isArray(mediaData?.data) ? ((mediaData?.data as unknown) as IMedia[]) : [];
    return { ...indexMedia(list), ...pickedMedia };
  }, [mediaData, pickedMedia]);

  // Hydrate local state from server payload.
  useEffect(() => {
    const t = (data as any)?.data;
    if (!t) return;
    setTemplate({
      id: t.id,
      name: t.name,
      orientation: t.orientation,
      canvas_width: t.canvas_width,
      canvas_height: t.canvas_height,
      background: t.background || "#000000",
      version: t.version,
      is_active: !!t.is_active,
      zones: (t.zones ?? []).map((z: any) => ({
        id: z.id,
        _uid: nextUid(),
        name: z.name ?? null,
        x: Number(z.x), y: Number(z.y), w: Number(z.w), h: Number(z.h),
        z_order: Number(z.z_order ?? 0),
        widget_type: z.widget_type,
        widget_config: z.widget_config || defaultConfigFor(z.widget_type),
      })),
    });
    setDirty(false);
  }, [data]);

  const selectedZone = useMemo(
    () => template?.zones.find((z) => z._uid === selectedUid) ?? null,
    [template, selectedUid],
  );

  const patchTemplate = (patch: Partial<DesignerTemplate>) => {
    setTemplate((t) => (t ? { ...t, ...patch } : t));
    setDirty(true);
  };

  const patchZone = (uid: string, patch: Partial<DesignerZone>) => {
    setTemplate((t) => {
      if (!t) return t;
      return { ...t, zones: t.zones.map((z) => z._uid === uid ? { ...z, ...patch } : z) };
    });
    setDirty(true);
  };

  const addZone = (type: WidgetType) => {
    setTemplate((t) => {
      if (!t) return t;
      const newZone: DesignerZone = {
        _uid: nextUid(),
        name: null,
        x: 10, y: 10, w: 40, h: 30,
        z_order: t.zones.length,
        widget_type: type,
        widget_config: defaultConfigFor(type),
      };
      setSelectedUid(newZone._uid);
      return { ...t, zones: [...t.zones, newZone] };
    });
    setDirty(true);
  };

  // ── Layout presets + undo ────────────────────────────────────────────
  // Snapshot the current zones so any destructive layout action can be undone.
  const pushUndo = (zones: DesignerZone[]) =>
    setUndoStack((s) => [...s.slice(-19), zones]); // cap history at 20 steps

  const undo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setTemplate((t) => (t ? { ...t, zones: prev } : t));
    setSelectedUid(null);
    setInteractUid(null);
    setDirty(true);
  };

  const presetToZones = (preset: LayoutPreset, baseZ: number): DesignerZone[] =>
    preset.zones.map((pz) => ({
      _uid: nextUid(),
      name: pz.name,
      x: pz.x, y: pz.y, w: pz.width, h: pz.height,
      z_order: baseZ + pz.zIndex,
      widget_type: pz.widgetType,
      // Merge any preset-specific overrides (e.g. VM POS menu source) over
      // the widget's default config.
      widget_config: { ...defaultConfigFor(pz.widgetType), ...(pz.config ?? {}) },
    }));

  // Empty canvas → apply immediately; otherwise ask before replacing.
  const requestPreset = (preset: LayoutPreset) => {
    if (!template) return;
    if (template.zones.length === 0) {
      applyPresetReplace(preset);
    } else {
      setPendingPreset(preset);
    }
  };

  const applyPresetReplace = (preset: LayoutPreset) => {
    if (!template) return;
    pushUndo(template.zones);
    const zones = presetToZones(preset, 0);
    setTemplate((t) => (t ? { ...t, zones } : t));
    setSelectedUid(zones[0]?._uid ?? null);
    setInteractUid(null);
    setDirty(true);
    setPendingPreset(null);
  };

  const applyPresetAdd = (preset: LayoutPreset) => {
    if (!template) return;
    pushUndo(template.zones);
    const baseZ = template.zones.reduce((m, z) => Math.max(m, z.z_order), -1) + 1;
    const added = presetToZones(preset, baseZ);
    setTemplate((t) => (t ? { ...t, zones: [...t.zones, ...added] } : t));
    setSelectedUid(added[0]?._uid ?? null);
    setDirty(true);
    setPendingPreset(null);
  };

  // Route a generated Smart Layout through the same apply flow (replace
  // confirmation + undo) as the fixed presets.
  const applySmartLayout = (name: string, zones: PresetZone[]) => {
    setSmartOpen(false);
    requestPreset({ id: "smart", name, category: "basic", zones });
  };

  const applyAiTemplate = (name: string, zones: PresetZone[]) => {
    setAiOpen(false);
    requestPreset({ id: "ai", name, category: "basic", zones });
  };

  const removeZone = (uid: string) => {
    setTemplate((t) => {
      if (!t) return t;
      return { ...t, zones: t.zones.filter((z) => z._uid !== uid) };
    });
    setSelectedUid(null);
    setInteractUid(null);
    setDirty(true);
  };

  // Selecting a different zone always exits embedded-content interaction.
  const selectZone = (uid: string | null) => {
    setSelectedUid(uid);
    if (uid !== interactUid) setInteractUid(null);
  };

  const handleOrientation = (next: "landscape" | "portrait") => {
    const o = ORIENTATIONS.find((x) => x.value === next);
    if (!o || !template) return;
    patchTemplate({
      orientation: o.value, canvas_width: o.w, canvas_height: o.h,
    });
  };

  const save = async () => {
    if (!template) return;
    // Block save while any image/video zone has no selected media. The
    // player cannot render such a zone, so we surface a review list instead
    // of persisting a broken template.
    const found = validateZones(template.zones);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    try {
      // Persist meta first (name / orientation / canvas / background).
      await updateTemplate({
        id: template.id,
        body: {
          name: template.name,
          orientation: template.orientation,
          canvas_width: template.canvas_width,
          canvas_height: template.canvas_height,
          background: template.background,
          is_active: template.is_active,
        },
      }).unwrap();

      // Then the zone tree.
      await replaceZones({
        id: template.id,
        zones: template.zones.map((z, i) => ({
          name: z.name,
          x: z.x, y: z.y, w: z.w, h: z.h,
          z_order: z.z_order ?? i,
          widget_type: z.widget_type,
          widget_config: z.widget_config,
        })),
      }).unwrap();

      showMessage("Template saved");
      setDirty(false);
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Could not save template"));
    }
  };

  if (isLoading || !template) {
    return <PageLoader />;
  }

  return (
    <Box sx={{ p: 2, bgcolor: PAGE_BG, minHeight: "calc(100vh - 64px)" }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5, mb: 2,
        flexWrap: "wrap",
      }}>
        <IconButton onClick={() => navigate("/templates/list")}><ArrowBackIcon /></IconButton>

        {/* Company is fixed for the life of the template — shown read-only so
            the designer's company context (media, playlists) is unambiguous
            and cross-company references can't be introduced by editing. */}
        <TextField
          size="small" label="Company"
          value={(data as any)?.data?.company?.name ?? "—"}
          InputProps={{ readOnly: true }}
          disabled
          sx={{ minWidth: 200 }}
        />

        <TextField
          size="small" label="Name" value={template.name}
          onChange={(e) => patchTemplate({ name: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select size="small" label="Orientation"
          value={template.orientation}
          onChange={(e) => handleOrientation(e.target.value as "landscape" | "portrait")}
          sx={{ minWidth: 220 }}
        >
          {ORIENTATIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Background" type="color"
          value={template.background}
          onChange={(e) => patchTemplate({ background: e.target.value })}
          sx={{ width: 110 }}
        />
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, ml: 1 }}>
          v{template.version} · {template.canvas_width}×{template.canvas_height}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={undoStack.length ? "Undo last layout change" : "Nothing to undo"}>
          <span>
            <Button
              variant="outlined" startIcon={<UndoIcon />}
              onClick={undo} disabled={undoStack.length === 0}
            >
              Undo
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)}
          disabled={dirty}
          title={dirty ? "Save first to preview the latest version" : "Preview"}
        >
          Preview
        </Button>
        <Button
          variant="contained" color="secondary"
          startIcon={<SaveIcon />} onClick={save}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </Button>
      </Box>

      {/* 3-col layout: palette | canvas | inspector */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "200px 1fr 320px",
        gap: 2, alignItems: "start",
      }}>
        {/* Palette */}
        <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${BORDER}` }}>
          {/* Widgets | Layouts tab switch */}
          <Box sx={{ display: "flex", gap: 0.5, mb: 1.5,
            border: `1px solid ${BORDER}`, borderRadius: 1, p: 0.25 }}>
            {([
              { key: "widgets" as const, label: "Widgets", icon: <WidgetsIcon sx={{ fontSize: 15 }} /> },
              { key: "layouts" as const, label: "Layouts", icon: <ViewQuiltIcon sx={{ fontSize: 15 }} /> },
            ]).map((tab) => (
              <Button key={tab.key} size="small" startIcon={tab.icon}
                onClick={() => setPaletteTab(tab.key)}
                sx={{
                  flex: 1, minWidth: 0, textTransform: "none", fontSize: 12, fontWeight: 700,
                  color: paletteTab === tab.key ? NAVY : TEXT_SECONDARY,
                  bgcolor: paletteTab === tab.key ? "rgba(212,175,55,0.12)" : "transparent",
                }}>
                {tab.label}
              </Button>
            ))}
          </Box>

          {paletteTab === "widgets" ? (
            <>
              {WIDGET_TYPES.map((t) => (
                <Tooltip key={t} placement="right" title={`Add ${WIDGET_LABELS[t]} zone`}>
                  <Button
                    fullWidth size="small" startIcon={<AddIcon />}
                    onClick={() => addZone(t)}
                    sx={{ justifyContent: "flex-start", color: TEXT_PRIMARY, mb: 0.5 }}
                  >
                    {WIDGET_LABELS[t]}
                  </Button>
                </Tooltip>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                Tip: drag a zone by its body, resize using the corner /
                edge handles. Hold the inspector slider to change layer order.
              </Typography>
            </>
          ) : (
            <LayoutPresetsPanel
              orientation={template.orientation}
              onApply={requestPreset}
              onSmartLayout={() => setSmartOpen(true)}
              onAiTemplate={() => setAiOpen(true)}
            />
          )}
        </Paper>

        {/* Canvas */}
        <Box>
          <Canvas
            width={template.canvas_width}
            height={template.canvas_height}
            background={template.background}
            zones={template.zones}
            selectedUid={selectedUid}
            interactUid={interactUid}
            onSelect={selectZone}
            onZoneChange={patchZone}
            mediaById={mediaById}
          />
          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1 }}>
            16:9 preview · zones positioned in % of canvas.
          </Typography>
        </Box>

        {/* Inspector */}
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}` }}>
          <Inspector
            zone={selectedZone}
            onChange={(patch) => selectedZone && patchZone(selectedZone._uid, patch)}
            onDelete={() => selectedZone && removeZone(selectedZone._uid)}
            interacting={!!selectedZone && interactUid === selectedZone._uid}
            onToggleInteract={() => selectedZone && setInteractUid(
              interactUid === selectedZone._uid ? null : selectedZone._uid,
            )}
            companyId={companyId}
            mediaById={mediaById}
            onMediaResolved={(m) => setPickedMedia((p) => ({ ...p, [m.id]: m }))}
          />
        </Paper>
      </Box>

      <TemplatePreviewDialog
        templateId={previewOpen ? template.id : null}
        onClose={() => setPreviewOpen(false)}
      />

      <SmartLayoutDialog
        open={smartOpen}
        orientation={template.orientation}
        onClose={() => setSmartOpen(false)}
        onApply={applySmartLayout}
      />

      <AiTemplateDialog
        open={aiOpen}
        orientation={template.orientation}
        onClose={() => setAiOpen(false)}
        onApply={applyAiTemplate}
      />

      {/* Apply-preset confirmation. Empty canvases skip this (applied
          immediately); when zones exist we never silently delete work. */}
      <Dialog open={!!pendingPreset} onClose={() => setPendingPreset(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply layout preset?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, mb: 1 }}>
            <strong>{pendingPreset?.name}</strong>
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
            Replacing swaps out the current {template.zones.length} zone
            {template.zones.length === 1 ? "" : "s"}. Adding keeps them — available
            only when the new zones don't overlap existing ones.
          </Typography>
          {pendingPreset && !presetCanBeAdded(
            pendingPreset,
            template.zones.map((zz) => ({ x: zz.x, y: zz.y, w: zz.w, h: zz.h })),
          ) && (
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 1, fontStyle: "italic" }}>
              "Add" is disabled because this preset's zones would overlap your
              current layout.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          <Button onClick={() => setPendingPreset(null)}>Cancel</Button>
          <Button
            disabled={
              !pendingPreset ||
              !presetCanBeAdded(
                pendingPreset,
                template.zones.map((zz) => ({ x: zz.x, y: zz.y, w: zz.w, h: zz.h })),
              )
            }
            onClick={() => pendingPreset && applyPresetAdd(pendingPreset)}
          >
            Add to current
          </Button>
          <Button variant="contained" color="secondary"
            onClick={() => pendingPreset && applyPresetReplace(pendingPreset)}>
            Replace Layout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save-blocking validation summary. Clicking a row selects + focuses
          the offending zone so the operator can fix it. */}
      <Dialog open={!!problems} onClose={() => setProblems(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: ERROR }}>
          <ErrorOutlineIcon /> Template cannot be saved
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 1 }}>
            {problems?.length === 1
              ? "1 zone needs attention:"
              : `${problems?.length ?? 0} zones need attention:`}
          </Typography>
          <List dense disablePadding>
            {problems?.map((p) => (
              <ListItemButton key={p.uid}
                onClick={() => { selectZone(p.uid); setProblems(null); }}
                sx={{ borderRadius: 1, mb: 0.5, border: `1px solid ${BORDER}` }}>
                <ListItemText
                  primary={p.label}
                  secondary={p.message}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}
                  secondaryTypographyProps={{ fontSize: 12, color: ERROR }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProblems(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEdit;
